import type { MotionProps } from "framer-motion";
import { Download, FileText } from "lucide-react";
import type { InfoLinksConfig } from "@/types/agent/info-links";
import { isSafeUrl, LINK_ICON_MAP } from "./infoLinkIcons";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";
import type { GenieIconName } from "@/components/ui/genie-icons/genie-icons";

/**
 * Shared building blocks for the Genie's full-screen "poof" surfaces — the
 * Details panel and the end-of-conversation wrap-up. Kept in one place so the
 * two surfaces stay pixel-identical instead of drifting apart.
 */

/** The full-screen dissolve-and-bloom entrance shared by both surfaces. */
export const POOF_MOTION: MotionProps = {
  initial: { opacity: 0, y: 16, filter: "blur(22px)" },
  animate: { opacity: 1, y: 0, filter: "blur(0px)" },
  exit: { opacity: 0, y: -12, filter: "blur(22px)" },
  transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1], filter: { duration: 0.6, ease: "easeOut" } },
};

/** Staggered section entrance — wisps lead, content condenses a beat later. */
export const staggerIn = (i: number): MotionProps => ({
  initial: { opacity: 0, y: 16, filter: "blur(6px)" },
  animate: { opacity: 1, y: 0, filter: "blur(0px)" },
  transition: { duration: 0.4, ease: "easeOut", delay: 0.18 + i * 0.07 },
});

export const formatSize = (bytes?: number): string | null => {
  if (!bytes || bytes <= 0) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/** A titled section with a gradient brand-icon header. */
export const PanelSection = ({
  genieIcon,
  title,
  brandColor,
  secondaryColor,
  children,
}: {
  genieIcon: GenieIconName;
  title: string;
  brandColor: string;
  secondaryColor: string;
  children: React.ReactNode;
}) => (
  <section className="rounded-2xl border border-[#E9EDF3] bg-white p-4 shadow-[0_10px_30px_-22px_rgba(15,23,42,0.35)]">
    <div className="mb-3 flex items-center gap-2.5">
      <span className="flex h-8 w-8 items-center justify-center rounded-lg" style={{ background: `${brandColor}12` }}>
        <GenieIcon name={genieIcon} size={16} color={brandColor} secondaryColor={secondaryColor} gradientEnabled />
      </span>
      <h3 className="text-[14px] font-semibold text-[#222B45]">{title}</h3>
    </div>
    {children}
  </section>
);

/**
 * Renders the take-away documents + links from an `info_links` config as a
 * stack of tappable cards. Returns null when there's nothing safe to show.
 */
export const ResourceLinks = ({
  resources,
  brandColor,
}: {
  resources?: InfoLinksConfig | null;
  brandColor: string;
}) => {
  const documents = (resources?.documents ?? []).filter((d) => d.fileUrl && isSafeUrl(d.fileUrl));
  const links = (resources?.links ?? []).filter((l) => isSafeUrl(l.url));
  if (documents.length === 0 && links.length === 0) return null;

  return (
    <div className="space-y-2">
      {documents.map((doc) => {
        const size = formatSize(doc.sizeBytes);
        return (
          <a
            key={doc.id}
            href={doc.fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            download={doc.fileName || undefined}
            className="group flex items-center gap-3 rounded-xl border border-[#E9EDF3] bg-white p-3 transition-all hover:-translate-y-[1px] hover:shadow-[0_8px_24px_-12px_rgba(15,23,42,0.25)]"
          >
            <span
              className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg"
              style={{ backgroundColor: `${brandColor}14`, color: brandColor }}
            >
              <FileText size={18} strokeWidth={2} />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium text-[#222B45]">{doc.label || doc.fileName}</span>
              {size && <span className="block text-xs text-[#9AA7B6]">{size}</span>}
            </span>
            <span
              className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full"
              style={{ color: brandColor }}
              aria-hidden
            >
              <Download size={16} strokeWidth={2.25} />
            </span>
          </a>
        );
      })}
      {links.map((link) => {
        const Icon = LINK_ICON_MAP[link.icon ?? ""] ?? LINK_ICON_MAP.link;
        return (
          <a
            key={link.id}
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-3 rounded-xl border border-[#E9EDF3] bg-white p-3 transition-all hover:-translate-y-[1px] hover:shadow-[0_8px_24px_-12px_rgba(15,23,42,0.25)]"
          >
            <span
              className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg"
              style={{ backgroundColor: `${brandColor}14`, color: brandColor }}
            >
              <Icon size={18} />
            </span>
            <span className="min-w-0 flex-1 truncate text-sm font-medium text-[#222B45]">{link.label}</span>
          </a>
        );
      })}
    </div>
  );
};
